import { signInAnonymously } from './authUtils';
import { Room, Participant, Vote, CardDeckType } from './types';
import { db } from '../firebase/firebase';

// Generate a random 6-character room code
export const generateRoomCode = (): string => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
};

// Predefined card decks
export const CARD_DECKS = {
    fibonacci: ['0', '1', '2', '3', '5', '8', '13', '21', '34', '?'],
    bau: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'],
    tshirt: ['XXS', 'XS', 'S', 'M', 'L', 'XL', '?'],
    powers: ['0', '1', '2', '4', '8', '16', '32', '64', '?'],
    custom: [], // This will be populated when creating a custom deck
};

export const createRoom = async (
    roomName: string,
    creatorName: string,
    deckType: CardDeckType = 'fibonacci',
    customValues: string[] = [],
): Promise<{ roomCode: string; userId: string }> => {
    const userId = await signInAnonymously();

    const roomCode = generateRoomCode();

    let cardValues = [...CARD_DECKS[deckType]];
    if (deckType === 'custom' && customValues.length > 0) {
        cardValues = Array.from(new Set(customValues));
    } else if (deckType === 'custom') {
        deckType = 'fibonacci';
        cardValues = [...CARD_DECKS[deckType]];
    }

    const roomData: Room = {
        id: roomCode,
        name: roomName,
        createdAt: Date.now(),
        creatorId: userId,
        currentRound: null,
        rounds: {},
        participants: {
            [userId]: {
                id: userId,
                name: creatorName,
                isActive: true,
                lastSeen: Date.now(),
                isAdmin: true,
            },
        },
        cardDeck: {
            type: deckType,
            values: cardValues,
        },
    };

    await db.ref(`rooms/${roomCode}`).set(roomData);

    localStorage.setItem(`pp_lastRoom`, roomCode);
    localStorage.setItem(`pp_${roomCode}_userName`, creatorName);

    return { roomCode, userId };
};

export const joinRoom = async (
    roomCode: string,
    userName: string,
): Promise<string> => {
    const userId = await signInAnonymously();

    const roomRef = db.ref(`rooms/${roomCode}`);
    const roomSnapshot = await roomRef.once('value');

    if (!roomSnapshot.exists()) {
        throw new Error('Room not found');
    }

    const participantRef = roomRef.child(`participants/${userId}`);
    const participantSnapshot = await participantRef.once('value');

    if (participantSnapshot.exists()) {
        await participantRef.update({
            name: userName,
            isActive: true,
            lastSeen: Date.now(),
        });
    } else {
        const participant: Participant = {
            id: userId,
            name: userName,
            isActive: true,
            lastSeen: Date.now(),
            isAdmin: false,
        };

        await roomRef.child(`participants/${userId}`).set(participant);
    }

    // Set up presence handlers for disconnect
    await participantRef.onDisconnect().update({
        isActive: false,
        lastSeen: Date.now(),
    });

    // Store the room code in localStorage for convenience
    localStorage.setItem(`pp_lastRoom`, roomCode);
    localStorage.setItem(`pp_${roomCode}_userName`, userName);

    return userId;
};

export const updateCardDeck = async (
    roomCode: string,
    deckType: CardDeckType,
    customValues: string[] = [],
): Promise<void> => {
    let cardValues = [...CARD_DECKS[deckType]];
    if (deckType === 'custom' && customValues.length > 0) {
        cardValues = Array.from(new Set(customValues));
    } else if (deckType === 'custom') {
        deckType = 'fibonacci';
        cardValues = [...CARD_DECKS[deckType]];
    }

    await db.ref(`rooms/${roomCode}/cardDeck`).set({
        type: deckType,
        values: cardValues,
    });
};

export const startNewRound = async (
    roomCode: string,
    description: string,
): Promise<string> => {
    const roundId = `round_${Date.now()}`;
    const roundData = {
        id: roundId,
        description,
        revealed: false,
        startedAt: Date.now(),
        votes: {},
    };

    await db.ref(`rooms/${roomCode}/rounds/${roundId}`).set(roundData);
    await db.ref(`rooms/${roomCode}/currentRound`).set(roundId);

    return roundId;
};

export const submitVote = async (
    roomCode: string,
    roundId: string,
    userId: string,
    value: string,
): Promise<void> => {
    const vote: Vote = {
        userId,
        value,
        timestamp: Date.now(),
    };

    await db
        .ref(`rooms/${roomCode}/rounds/${roundId}/votes/${userId}`)
        .set(vote);
};

export const revealVotes = async (
    roomCode: string,
    roundId: string,
): Promise<void> => {
    await db.ref(`rooms/${roomCode}/rounds/${roundId}/revealed`).set(true);
};

export const updateUserActivity = async (
    roomCode: string,
    userId: string,
): Promise<void> => {
    try {
        const timestamp = Date.now();

        const updates = {
            [`/rooms/${roomCode}/participants/${userId}/lastSeen`]: timestamp,
            [`/rooms/${roomCode}/participants/${userId}/isActive`]: true,
        };

        await db.ref().update(updates);

        // Set up presence handlers
        const userStatusRef = db.ref(
            `/rooms/${roomCode}/participants/${userId}`,
        );

        // When user disconnects, update their status
        await userStatusRef.onDisconnect().update({
            isActive: false,
            lastSeen: timestamp,
        });
    } catch (error) {
        console.error('Error updating user activity:', error);
        throw error;
    }
};
